import type { Task } from '@dyspatch/core';
import { isSyntheticKey } from '@/lib/briefs';

/**
 * Whether this job can be integration checked, and why not when it cannot
 * (issue #26).
 *
 * Mirrored from core's `planIntegrationCheck` rather than imported, for the
 * reason `merge-order.ts` and `notes.ts` give: a value import of
 * `@dyspatch/core` pulls the barrel, and `node:crypto` with it, into the
 * browser bundle. `packages/hub/test/integration-mirror.test.ts` pins the two
 * together over the cases that matter, so the button never offers a verb the
 * hub answers 409 to and never refuses one it would have taken.
 *
 * Only the answer is mirrored, not the branch order: the console draws the
 * branches off the verdict the hub sent, and the merge order is the worker's
 * business.
 */
export interface CheckReadiness {
  ready: boolean;
  reason: string | null;
  /** How many branches the check would merge. Zero whenever it is not ready. */
  branches: number;
}

const SETTLED = ['review', 'done'];

export function checkReadiness(tasks: readonly Task[]): CheckReadiness {
  const steps = tasks.filter((task) => !isSyntheticKey(task.key) && task.status !== 'cancelled');
  if (steps.length === 0) {
    return { ready: false, reason: 'this job has no tasks to check yet', branches: 0 };
  }

  const working = steps.filter((task) => !SETTLED.includes(task.status));
  if (working.length > 0) {
    const keys = working.map((task) => task.key).join(', ');
    return {
      ready: false,
      reason: `every task has to be in review or done first; ${working.length} still working (${keys})`,
      branches: 0,
    };
  }

  const open = steps.filter((task) => task.status === 'review' && task.branch);
  if (open.length === 0) {
    return {
      ready: false,
      reason: 'nothing is waiting to merge, so there is no combination left to test',
      branches: 0,
    };
  }

  return { ready: true, reason: null, branches: open.length };
}

/** The check step the fleet is working on right now, if there is one. */
export function runningCheck(tasks: readonly Task[]): Task | null {
  const open = tasks.filter(
    (task) =>
      /^CHECK(-[1-9][0-9]*)?$/.test(task.key) &&
      task.status !== 'done' &&
      task.status !== 'failed' &&
      task.status !== 'cancelled',
  );
  open.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return open[0] ?? null;
}
